import React, { useEffect, useState } from "react";
import { X, Heart, ExternalLink, Rocket } from "lucide-react";

const KEY = "sat_promo_last_shown";
const INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const UPVOTE_URL =
  "https://app.emergent.sh/showcase/builderfest-kevin/ac7f8b56-4abb-4269-bef4-834075890000?ref=sgex649359&utm_source=share";

/** Recurring upvote-us modal. Shows once per session tab immediately,
 *  then every 15 minutes while the tab stays open. Persists across
 *  sessions via localStorage timestamp. Works for logged-in AND
 *  anonymous visitors — never gated behind auth. */
export default function SupportPromoModal() {
  const [open, setOpen] = useState(false);

  const maybeShow = () => {
    // Always show on first visit; otherwise honour the 15-min cool-down.
    const last = parseInt(localStorage.getItem(KEY) || "0", 10);
    if (!last || Date.now() - last > INTERVAL_MS) setOpen(true);
  };

  useEffect(() => {
    // Small delay so BootSequence + TermsGate settle first.
    const t = setTimeout(maybeShow, 3200);
    const id = setInterval(maybeShow, INTERVAL_MS);
    return () => { clearTimeout(t); clearInterval(id); };
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, String(Date.now()));
    setOpen(false);
  };

  const upvote = () => {
    localStorage.setItem(KEY, String(Date.now()));
    window.open(UPVOTE_URL, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={dismiss}
      data-testid="promo-backdrop"
    >
      <div
        className="sat-brackets sat-surface border border-[color:var(--sat-cyan)] p-6 md:p-8 w-full max-w-lg relative sat-box-glow"
        onClick={(e) => e.stopPropagation()}
        data-testid="promo-modal"
      >
        <button
          onClick={dismiss}
          data-testid="promo-close"
          aria-label="Close promo"
          className="absolute top-3 right-3 text-[color:var(--sat-text-dim)] hover:sat-cyan"
        >
          <X size={16} />
        </button>

        <div className="sat-label sat-cyan flex items-center gap-2 mb-3">
          <Heart size={12} /> DIRECTIVE // COMMUNITY SUPPORT
        </div>

        <h2 className="font-display font-bold text-2xl md:text-3xl leading-tight mb-3">
          Support <span className="sat-cyan">AstronAcademics</span>
        </h2>

        <p className="text-sm md:text-base leading-relaxed text-[color:var(--sat-text-dim)]">
          Our educational initiative for <span className="text-white">students, learners, educators, and curious minds</span>.
          A single upvote helps us keep the servers running and the mission alive.
        </p>

        <div className="mt-5 border border-[color:var(--sat-border)] p-4 space-y-2">
          <div className="sat-label sat-cyan flex items-center gap-2">
            <Rocket size={11} /> HOW TO HELP
          </div>
          <ol className="text-sm space-y-1 list-decimal list-inside text-[color:var(--sat-text-dim)]">
            <li>Tap <span className="sat-cyan">UPVOTE US</span> below.</li>
            <li>Create a free Emergent account (takes 15 seconds).</li>
            <li>Hit the upvote button on the showcase page — that's it.</li>
          </ol>
        </div>

        <div className="mt-6 flex flex-col md:flex-row gap-3">
          <button
            onClick={upvote}
            data-testid="promo-upvote"
            className="sat-btn sat-btn-solid flex-1 justify-center !py-3"
          >
            <Heart size={13} /> UPVOTE US <ExternalLink size={11} />
          </button>
          <button
            onClick={dismiss}
            data-testid="promo-later"
            className="sat-btn !py-3 !px-4"
          >
            NOT NOW
          </button>
        </div>

        <div className="sat-label text-[9px] text-[color:var(--sat-text-dim)] mt-4 leading-relaxed">
          THIS REMINDER RE-APPEARS EVERY 15 MINUTES · YOU CAN ALWAYS CLOSE IT
        </div>
      </div>
    </div>
  );
}
